import {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type ReactElement,
} from 'react';

// Components
import { Col, Row } from 'antd';
import { Navbar } from './components/Navbar';
import { Library } from './components/Library';
import PlayingBar from './components/PlayingBar';
import { PlayingNow } from './components/NowPlaying';
import { LanguageModal } from '../Modals/LanguageModal';
import { LibraryDrawer } from '../Drawers/LibraryDrawer';
import { PlayingNowDrawer } from '../Drawers/PlayingNowDrawer';
import { EditPlaylistModal } from '../Modals/EditPlaylistModal';
import { ImportOnboardingModal } from '../Modals/ImportOnboardingModal';
import { ImportCompletionModal } from '../Modals/ImportCompletionModal';
import { FeedbackModal } from '../Feedback/FeedbackModal';
import { FeedbackButton } from '../Feedback/FeedbackButton';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

// Interfaces

// Redux
import { useAppDispatch, useAppSelector } from '../../store/store';
import { isActiveOnOtherDevice, spotifyActions } from '../../store/slices/spotify';
import {
  getLibraryCollapsed,
  getLibraryExpanded,
  isRightLayoutOpen,
  uiActions,
} from '../../store/slices/ui';
import { LoginFooter } from './components/LoginFooter';
import { LoginModal } from '../Modals/LoginModal';
import useIsMobile from '../../utils/isMobile';

export const AppLayout: FC<{ children: ReactElement }> = memo((props) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => !!state.auth.user);
  const rightLayoutOpen = useAppSelector(isRightLayoutOpen);
  const libraryCollapsed = useAppSelector(getLibraryCollapsed);
  const libraryExpanded = useAppSelector(getLibraryExpanded);
  const activeOnOtherDevice = useAppSelector(isActiveOnOtherDevice);

  const [isTablet, setIsTablet] = useState(false);
  const [footerHeight, setFooterHeight] = useState<number | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);

  const isMobile = useIsMobile();

  useEffect(() => {
    window.onresize = () => {
      const vh = window.innerWidth;
      if (vh < 950) {
        dispatch(uiActions.collapseLibrary());
        setIsTablet(true);
      } else {
        setIsTablet(false);
      }
    };
    return () => {
      window.onresize = null;
    };
  }, [dispatch]);

  // Removed getAvailableDevices from startup - it's not needed for player initialization
  // Devices are now only fetched when the device picker UI is opened (see Devices component)
  // This saves 1-2s on startup and removes a network dependency from the critical path

  type PanelSizing = {
    minSize: number;
    maxSize: number;
    defaultSize: number;
    minWidth: number;
    maxWidth?: number;
    width?: number | string;
    flexGrow?: number;
  };

  const leftPanelConfig = (): PanelSizing => {
    if (isTablet) {
      return {
        minSize: 10,
        maxSize: 10,
        defaultSize: 10,
        minWidth: 85,
        maxWidth: 320,
      };
    }

    if (libraryCollapsed) {
      return {
        minSize: 7,
        maxSize: 8,
        defaultSize: 7,
        minWidth: 85,
        maxWidth: 85,
      };
    }

    if (libraryExpanded) {
      return {
        minSize: 100,
        maxSize: 100,
        defaultSize: 100,
        minWidth: 280,
        width: '100%',
        flexGrow: 1,
      };
    }

    return {
      minSize: 26,
      maxSize: 45,
      defaultSize: 34,
      minWidth: 360,
    };
  };

  const panelSizing = leftPanelConfig();

  const panelGroupKey = libraryExpanded ? 'library-expanded' : 'library-default-v3';
  const baseFooterHeight = activeOnOtherDevice ? 141 : !user && isMobile ? 0 : 105;
  const effectiveFooterHeight = Math.max(footerHeight ?? 0, baseFooterHeight);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const contentHeight = `calc(100vh - (${effectiveFooterHeight}px + var(--youtube-mini-player-height, 0px)))`;
  const spinnerMinHeight =
    navbarHeight > 0
      ? `calc(100vh - (${effectiveFooterHeight}px + ${navbarHeight}px + var(--youtube-mini-player-height, 0px)))`
      : contentHeight;
  const rowStyle: CSSProperties & { '--spinner-min-height'?: string } = {
    overflow: 'hidden',
    height: contentHeight,
    '--spinner-min-height': spinnerMinHeight,
  };

  useLayoutEffect(() => {
    const measureNavbar = () => {
      const el = document.querySelector<HTMLElement>('.navbar');
      if (!el) return;
      setNavbarHeight(Math.round(el.getBoundingClientRect().height));
    };

    measureNavbar();
    window.addEventListener('resize', measureNavbar);
    return () => window.removeEventListener('resize', measureNavbar);
  }, []);

  useLayoutEffect(() => {
    const footerEl = footerRef.current;
    if (!footerEl) return;

    const measure = () => {
      const rect = footerEl.getBoundingClientRect();
      setFooterHeight(Math.max(0, Math.round(rect.height)));
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(footerEl);
    window.addEventListener('resize', measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [user, isMobile, activeOnOtherDevice]);

  return (
    <>
      {/* Modals & Drawers */}
      <LanguageModal />
      <LibraryDrawer />
      <PlayingNowDrawer />
      <EditPlaylistModal />
      <LoginModal />
      <ImportOnboardingModal />
      <ImportCompletionModal />
      <FeedbackModal />

      {/* Main Component */}
      <div className='main-container'>
        <Row
          wrap
          justify='end'
          gutter={[8, 0]}
          style={rowStyle}
        >
          <Col span={24}>
            <Navbar />
          </Col>

          <Col
            span={24}
            style={{
              maxHeight: activeOnOtherDevice
                ? `calc(100vh - (${effectiveFooterHeight}px + var(--youtube-mini-player-height, 0px)))`
                : undefined,
            }}
          >
              <PanelGroup
                key={panelGroupKey}
                direction='horizontal'
                autoSaveId={libraryExpanded ? undefined : 'library-panel-v3'}
              >
              <Panel
                id='left'
                order={1}
                className='mobile-hidden'
                minSize={panelSizing.minSize}
                maxSize={panelSizing.maxSize}
                defaultSize={panelSizing.defaultSize}
                style={{
                  borderRadius: 5,
                  minWidth: panelSizing.minWidth,
                  maxWidth: panelSizing.maxWidth,
                  width: panelSizing.width,
                  flexGrow: panelSizing.flexGrow,
                }}
              >
                <Library />
              </Panel>

              {!libraryExpanded && !isMobile ? <PanelResizeHandle className='resize-handler' /> : null}
              {!libraryExpanded ? (
                <Panel id='center' order={2} style={{ borderRadius: 5 }}>
                  {/* Home | Playlists */}
                  {props.children}
                </Panel>
              ) : null}

              {!libraryExpanded && !isTablet && rightLayoutOpen ? (
                <PanelResizeHandle className='resize-handler' />
              ) : null}

              {!libraryExpanded && rightLayoutOpen ? (
                <Panel
                  order={3}
                  minSize={23}
                  maxSize={30}
                  defaultSize={25}
                  id='details-section'
                  style={{ borderRadius: 5 }}
                >
                  <PlayingNow />
                </Panel>
              ) : null}
            </PanelGroup>
          </Col>
        </Row>
      </div>

      {<footer ref={footerRef}>{user ? <PlayingBar /> : <LoginFooter />}</footer>}
      <FeedbackButton footerHeight={effectiveFooterHeight} />
    </>
  );
});

AppLayout.displayName = 'AppLayout';
